import { useCallback } from "react";

import { RadioGroup, Text } from "@vericus/cadmus-ui";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot, setLocalSnapshot } from "@/features/authority";
import { Snapshot } from "@/stores/snapshot";

import * as styles from "./answer-form.css";

interface Props {
  /** Field identifier */
  identifier: string;
  /** Current Answer Block ID */
  answerBlockId: string;
  /** Make the choices readonly */
  isReadonly?: boolean;
}

/**
 * Answer response form section to respond to a True/False choice question.
 */
export function BooleanResponseForm(props: Props) {
  const { identifier, answerBlockId } = props;

  const dispatch = useAppDispatch();
  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const version = snapshot?.version ?? 0;
  const answerIsTrue = getAnswerBoolean(snapshot, props.identifier);

  // Callback to update the local snapshot on every change
  const setBoolean = useCallback(
    (value: boolean) => {
      const newVersion = version + 1;
      const snapshot: Snapshot = {
        version: newVersion,
        answerBoolean: value,
        answerFields: { [identifier]: [value.toString()] },
      };
      dispatch(
        setLocalSnapshot({
          answerBlockId,
          snapshot,
        })
      );
    },
    [dispatch, version, answerBlockId, identifier]
  );

  const choices = [
    { label: "True", value: true },
    { label: "False", value: false },
  ];

  return (
    <div className={styles.optionResponseForm}>
      <RadioGroup.Root
        value={answerIsTrue?.toString() ?? ""}
        onValueChange={(value) => setBoolean(value === "true")}
        disabled={props.isReadonly}
        className={styles.radioGroup}
      >
        {choices.map((choice) => (
          <RadioGroup.Card
            key={choice.label}
            value={choice.value.toString()}
            className={styles.radioCard}
          >
            <Text kind="bodyMd" as="div">
              {choice.label}
            </Text>
          </RadioGroup.Card>
        ))}
      </RadioGroup.Root>
    </div>
  );
}

function getAnswerBoolean(
  snapshot: Snapshot | undefined,
  identifier: string
): boolean | undefined {
  if (snapshot?.answerFields) {
    const values = snapshot.answerFields[identifier];
    if (values && values.length > 0) {
      return values[0] === "true";
    }
    return undefined;
  }
  return snapshot?.answerBoolean;
}
